import { string, object, boolean, number, array, } from 'yup';
import { AlternativeInvoicePdfFallback, ContactAddressLocation, CreditEffectOptions, SenderProfileAssigneeRecipient, PortalLineItemField, PortalPaidMaxAgeUnit, StatementLinkBehaviorTypes, StatementLogoPosition, WEBHOOK_EVENT_KEYS, } from '../../models';
const EMAIL_ERROR_POSTFIX = `must be a valid email address`;
function validateCommaSeparatedEmails(value, max) {
    if (value == null || value === '') {
        return true;
    }
    if (typeof value !== 'string') {
        return false;
    }
    const emails = value
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);
    if (emails.length === 0) {
        return true;
    }
    if (emails.length > max) {
        return false;
    }
    return emails.every(e => string().email().isValidSync(e));
}
function parseCommaSeparatedEmails(value) {
    if (typeof value !== 'string') {
        return [];
    }
    return value
        .split(',')
        .map(e => e.trim().toLowerCase())
        .filter(e => e.length > 0);
}
const addressSchema = object({
    lineOne: string().nullable().optional(),
    lineTwo: string().nullable().optional(),
    city: string().nullable().optional(),
    state: string().nullable().optional(),
    country: string().nullable().optional(),
    postalCode: string().nullable().optional(),
});
export const senderProfileSchema = object({
    id: number().optional(),
    default: boolean().required('Default Sender Profile setting is required'),
    name: string().required('Sender Profile Name is required'),
    bccAddress: string()
        .nullable()
        .optional()
        .test('bcc-email-list', 'BCC Address must be up to 3 valid emails, comma separated', v => validateCommaSeparatedEmails(v, 3)),
    ccAddress: string()
        .nullable()
        .optional()
        .test('cc-email-list', 'CC Address must be up to 3 valid emails, comma separated', v => validateCommaSeparatedEmails(v, 3)),
    assigneeRecipient: string().oneOf(Object.values(SenderProfileAssigneeRecipient)).nullable().optional(),
    replyToPaidniceMailbox: boolean().nullable().optional(),
    replyToAddress: string().email(`Reply To Address ${EMAIL_ERROR_POSTFIX}`).nullable().optional(),
    fromAddress: string().matches(/^(?!.*\.$)(?!^\.)[a-zA-Z0-9_.-]+$/, {
        message: `From Address must only include letters, numbers, hyphens, dots and underscores. (Do not include the @)`,
        excludeEmptyString: true,
    }).trim().nullable().optional(),
    signatureHtml: string().nullable().optional(),
    signatureJson: string().nullable().optional(),
    fromName: string().required('From Name Setting is required'),
    logoUrl: string().url('Logo URL must be a valid URL').nullable().optional(),
    hideLogo: boolean().nullable().optional(),
}).test('cc-bcc-no-overlap', 'BCC and CC cannot include the same email', v => {
    if (!(v === null || v === void 0 ? void 0 : v.bccAddress) || !(v === null || v === void 0 ? void 0 : v.ccAddress)) {
        return true;
    }
    const bcc = new Set(parseCommaSeparatedEmails(v.bccAddress));
    const cc = parseCommaSeparatedEmails(v.ccAddress);
    return cc.every(email => !bcc.has(email));
});
export const settingUpdateSchema = object({
    logoUrl: string().url().nullable().optional(),
    active: boolean().optional(),
    webhookEnabled: boolean().optional(),
    webhookUrl: string().url().nullable().optional(),
    webhookDisabledEvents: array().of(string().oneOf(WEBHOOK_EVENT_KEYS).required()).optional(),
    companyNumber: string().nullable().optional(),
    companyNumberType: string().nullable().optional(),
    taxNumber: string().nullable().optional(),
    companyContactNumber: string().nullable().optional(),
    companyEmail: string().email(`Company Email ${EMAIL_ERROR_POSTFIX}`).nullable().optional(),
    companyAddress: addressSchema.nullable().optional(),
    statementFooter: string().nullable().optional(),
    timezone: string().required(`A timezone is required`),
    autofreezeInvoice: boolean().required(`Autofreeze Invoice Setting is required`),
    setSendFlagLateFee: boolean().required(`Set Send Flag Late Fee Setting is required`),
    onlySendPrimaryContact: boolean().nullable().optional(),
    portalViewAllInvoices: boolean().required(`Portal View All Invoices Setting is required`),
    portalViewPaidInvoices: boolean().required(`Portal View Paid Invoices Setting is required`),
    portalPaidMaxAge: number().integer(`Paid invoice age must be a whole number`)
        .min(1, `Paid invoice age must be at least 1`)
        .nullable().optional(),
    portalPaidMaxAgeUnit: string().oneOf(Object.values(PortalPaidMaxAgeUnit)).optional(),
    portalShowQuotes: boolean().optional(),
    statementLogoPosition: string().oneOf(Object.values(StatementLogoPosition)).required(`Statement Logo Position Setting is required`),
    useDisplayName: boolean().required(`Use Display Name Setting is required`),
    statementAlwaysLegalName: boolean().required(`Statement Always Legal Name Setting is required`),
    useExpectedPaymentDate: boolean().nullable().optional(),
    primaryColor: string().min(7).max(7).nullable().optional(),
    syncNotesOnAction: boolean().required(`Sync notes on action is required`),
    agedAnalysisBreakpointOne: number()
        .min(1, `Aged Analysis Breakpoint One must be greater than 0`)
        .max(365, `Aged Analysis Breakpoint One must be less than 366`),
    agedAnalysisBreakpointTwo: number()
        .min(1, `Aged Analysis Breakpoint Two must be greater than 0`)
        .max(365, `Aged Analysis Breakpoint Two must be less than 366`)
        .nullable().optional(),
    agedAnalysisBreakpointThree: number()
        .min(1, `Aged Analysis Breakpoint Three must be greater than 0`)
        .max(365, `Aged Analysis Breakpoint Three must be less than 366`)
        .nullable().optional(),
    allowedQuotePOField: boolean().nullable().optional(),
    allowedQuotePOUpload: boolean().nullable().optional(),
    splitStatementOnDueIndex: number().optional(),
    splitStatementOnDue: boolean().nullable().optional(),
    statementHideAgedBreakdown: boolean().nullable().optional(),
    statementHideBalancesOverview: boolean().nullable().optional(),
    hideNonCustomers: boolean(),
    creditNoteEffect: string().oneOf(Object.values(CreditEffectOptions)).required(),
    prepaymentsEffect: string().oneOf(Object.values(CreditEffectOptions)).required(),
    overpaymentsEffect: string().oneOf(Object.values(CreditEffectOptions)).required(),
    statementColumns: array().of(object({
        type: string().required(),
        truncate: boolean().optional(),
    })).max(8).min(5),
    portalStatementColumns: array().of(object({
        type: string().required(),
        truncate: boolean().optional(),
    })).max(8).min(5).optional(),
    activityStartDate: string().nullable().optional(),
    activityEndDate: string().nullable().optional(),
    statementLinkBehavior: string().oneOf(Object.values(StatementLinkBehaviorTypes)).required(),
    contactAddressLocation: string().oneOf(Object.values(ContactAddressLocation)).nullable().optional(),
    contactAddressOffsetLeft: number()
        .min(-5, `Offset from left must be at least -5cm`)
        .max(10, `Offset from left must be at most 10cm`)
        .test(`one-decimal`, `Use 0.1cm increments`, v => v === undefined || v === null || Math.round(v * 10) === v * 10)
        .nullable().optional(),
    contactAddressOffsetTop: number()
        .min(-5, `Offset from top must be at least -5cm`)
        .max(10, `Offset from top must be at most 10cm`)
        .test(`one-decimal`, `Use 0.1cm increments`, v => v === undefined || v === null || Math.round(v * 10) === v * 10)
        .nullable().optional(),
    consolidateToEmails: boolean().nullable().optional(),
    displayCustomFields: boolean().nullable().optional(),
    separateJobsProjects: boolean().nullable().optional(),
    senderProfiles: array().of(senderProfileSchema).min(1, 'At least one Sender Profile is required').required('Sender Profiles are required'),
    paymentPlanNotesEnabled: boolean().nullable().optional(),
    requirePortalSecurity: boolean().nullable().optional(),
    portalPromisesEnabled: boolean().nullable().optional(),
    portalPromiseRequiresVerification: boolean().nullable().optional(),
    portalQueriesEnabled: boolean().nullable().optional(),
    portalQueryRequiresVerification: boolean().nullable().optional(),
    portalLineItemHiddenFields: array().of(string().oneOf(Object.values(PortalLineItemField)).required()).optional(),
    portalHideZeroLineAmounts: boolean().optional(),
    promiseGraceDays: number().integer(`Grace days must be a whole number`)
        .min(0, `Grace days must be at least 0`)
        .max(30, `Grace days must be at most 30`)
        .nullable().optional(),
    promiseUpdatesExpectedPaymentDate: boolean().nullable().optional(),
    useAlternativeInvoicePdf: boolean().optional(),
    alternativeInvoicePdfPrefix: string().nullable().optional(),
    alternativeInvoicePdfFallback: string().oneOf(Object.values(AlternativeInvoicePdfFallback)).optional(),
});
//# sourceMappingURL=settings.js.map